import { buildGatewayUrl } from '../api/config.js';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AI_TOOL_CONTRACT, EXTRA_SANDBOX_SCENARIOS } from './sandbox-playground-scenarios.js';
import { SANDBOX_BASE as SHARED_SANDBOX_BASE } from '../components/SandboxLogs.jsx';

/**
 * Playground —— 用户自己改参数、自己跑、把代码抄走。
 *
 * 三件事缺一不可，缺了就只是个演示：
 *   1. 每一步都是真实发出的请求，响应/耗时/花费现场产生，没有预置输出；
 *   2. 关键参数可编辑 —— 只能点「运行」的话，用户学不到参数怎么写；
 *   3. 每一步都能拿到填好真实值的 cURL / SDK 代码，直接贴进自己的项目。
 *
 * 展示上把「结果摘要」和「原始报文」分开：直接铺 JSON 会淹没重点，用户要看的是
 * exitCode 和 stdout，不是 offering 对象的三十个字段。
 */

const SANDBOX_BASE = SHARED_SANDBOX_BASE;
const PUBLIC_HOST = String(
  import.meta.env.VITE_SANDBOX_PUBLIC_HOST
    || (/^https?:/i.test(SANDBOX_BASE) ? SANDBOX_BASE : 'https://sandbox.xapi.to'),
).replace(/\/$/, '');
const KEY_STORAGE = 'xapi.sandbox.session-key';
// 普通 API 走的是另一套网关（每个服务一个 host 前缀），和 Sandbox 网关不是同一个地址。
const API_GATEWAY_TEMPLATE = String(
  import.meta.env.VITE_API_GATEWAY_TEMPLATE || buildGatewayUrl('{host}'),
);

export const pinSandboxGateway = (base, provider) => {
  if (!provider || !/^https?:/i.test(base)) return base;
  const url = new URL(base);
  const suffix = url.hostname.replace(/^sandbox\./, '');
  url.hostname = `${provider}.sandbox.${suffix}`;
  url.pathname = '';
  url.search = '';
  url.hash = '';
  return url.toString().replace(/\/$/, '');
};
const sandboxGateway = (provider) => pinSandboxGateway(SANDBOX_BASE, provider);

const money = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(7).replace(/0+$/, '').replace(/\.$/, '')}`;
  return `$${n.toFixed(4)}`;
};

const req = (method, path, body) => ({ method, path, body });
const rows = (...pairs) =>
  pairs.filter(([, v]) => v != null && v !== '').map(([k, v]) => ({ k, v }));

const DEFAULT_COMMAND = `python3 -c "
n = 20000
sieve = [True] * n
sieve[0] = sieve[1] = False
for i in range(2, int(n ** 0.5) + 1):
    if sieve[i]:
        for j in range(i * i, n, i):
            sieve[j] = False
print('primes below', n, '=', sum(sieve))"`;

const DEFAULT_HTML =
  '<!doctype html><meta charset="utf-8">\n<h1>Hello from xAPI Sandbox</h1>\n<p>这个页面是刚刚写进沙箱的。</p>';

const CORE_SCENARIOS = [
  {
    id: 'run-code',
    title: '跑一段代码，取回结果',
    blurb:
      '让平台挑一台机器、跑你写的命令、拿到 stdout、用完销毁。命令可以随便改 —— 下面跑的就是你填的那段。',
    params: [
      {
        key: 'command',
        label: '要执行的命令',
        type: 'textarea',
        default: DEFAULT_COMMAND,
        hint: '原样送进沙箱的 shell。改成你自己的，跑出来的就是你的结果。',
      },
      {
        key: 'capabilities',
        label: '要求的能力',
        type: 'text',
        default: 'exec',
        hint: '逗号分隔。平台只会在支持这些能力的服务商里挑，写 exec,files 就排除掉不支持文件的。',
      },
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '0.20' },
    ],
    steps: [
      {
        title: '让平台挑一家',
        note: '不指定服务商。平台在所有已上架的规格里，挑满足能力要求且最便宜的一个。',
        build: (ctx, p) => req('POST', '/v1/quotes', {
          requirements: {
            capabilities: String(p.capabilities || 'exec').split(',').map((s) => s.trim()).filter(Boolean),
          },
          maxEstimatedHourlyUsd: String(p.maxHourly || '0.20'),
        }),
        after: (res, ctx) => { ctx.quoteId = res?.quoteId; },
        summary: (res) => rows(
          ['选中', res?.offering?.name],
          ['运行单价', res?.offering?.billing?.estimatedHourlyUsdByState?.RUNNING
            ? `$${res.offering.billing.estimatedHourlyUsdByState.RUNNING}/时` : null],
        ),
      },
      {
        title: '开机',
        note: '这一刻开始计费。返回 202 表示已受理 —— 状态要等平台确认，不以按钮响应为准。',
        build: (ctx) => req('POST', '/v1/sandboxes', {
          selection: { quoteId: ctx.quoteId },
          idempotencyKey: `playground:run-code:${crypto.randomUUID()}`,
          metadata: { client: 'sandbox-playground', scenario: 'run-code' },
        }),
        after: (res, ctx) => {
          ctx.instanceId = res?.id;
          ctx.instanceIds = [...new Set([...(ctx.instanceIds || []), res?.id].filter(Boolean))];
        },
        summary: (res) => rows(['实例', res?.id], ['状态', res?.observedState]),
      },
      { wait: 'RUNNING', title: '等待就绪', note: '轮询到 RUNNING 才能执行命令。' },
      {
        title: '执行你的命令',
        build: (ctx, p) => req('POST', `/v1/sandboxes/${ctx.instanceId}/commands`, {
          command: p.command,
          timeoutSeconds: 60,
        }),
        summary: (res) => ({
          rows: rows(['exitCode', String(res?.exitCode ?? '')]),
          block: res?.stdout || res?.stderr || null,
        }),
      },
      {
        title: '销毁',
        note: '不销毁会一直按运行状态扣费。余额不足时平台会自动关停，但别指望它。',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/terminate`, {
          idempotencyKey: `playground:terminate:${crypto.randomUUID()}`,
        }),
      },
      { wait: 'TERMINATED', title: '确认销毁完成' },
    ],
  },
  {
    id: 'deploy',
    title: 'Web 预览与临时 API（Cloudflare）',
    gateway: 'cf-edge',
    blurb:
      '定向使用 Cloudflare Sandbox：写文件、起 HTTP 服务并取得公网地址。页面内容和端口都可以改。',
    params: [
      { key: 'html', label: '页面内容', type: 'textarea', default: DEFAULT_HTML,
        hint: '会被写到 /workspace/index.html。' },
      { key: 'port', label: '服务端口', type: 'text', default: '8080',
        hint: '起服务和取公网地址用的都是它。' },
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '0.20' },
    ],
    steps: [
      {
        title: '按 Web 能力取得报价',
        note: '按能力筛选：不支持 files / ports 的服务商会被自动排除。',
        build: (ctx, p) => req('POST', '/v1/quotes', {
          requirements: { capabilities: ['exec', 'files', 'ports'] },
          maxEstimatedHourlyUsd: String(p.maxHourly || '0.20'),
        }),
        after: (res, ctx) => { ctx.quoteId = res?.quoteId; },
        summary: (res) => rows(['服务商', res?.offering?.name], ['每小时', res?.estimatedHourlyUsd]),
      },
      {
        title: '创建 Web 环境',
        build: (ctx) => req('POST', '/v1/sandboxes', {
          selection: { quoteId: ctx.quoteId },
          idempotencyKey: `playground:web:${crypto.randomUUID()}`,
          metadata: { client: 'sandbox-playground', scenario: 'web-preview' },
        }),
        after: (res, ctx) => {
          ctx.instanceId = res?.id;
          ctx.instanceIds = [...new Set([...(ctx.instanceIds || []), res?.id].filter(Boolean))];
        },
        summary: (res) => rows(['实例', res?.id], ['状态', res?.observedState]),
      },
      { wait: 'RUNNING', title: '等待就绪' },
      {
        title: '写入页面',
        build: (ctx, p) => req('POST', `/v1/sandboxes/${ctx.instanceId}/files`, {
          path: 'index.html', encoding: 'utf8', content: p.html,
        }),
        summary: (res) => rows(['写入', res?.path]),
      },
      {
        title: '起 HTTP 服务',
        note: '后台运行，命令立刻返回。',
        build: (ctx, p) => req('POST', `/v1/sandboxes/${ctx.instanceId}/commands`, {
          command: `nohup python3 -m http.server ${Number(p.port) || 8080} >/tmp/serve.log 2>&1 & sleep 2; curl -sf http://127.0.0.1:${Number(p.port) || 8080}/ | head -c 200`,
          timeoutSeconds: 60,
        }),
        summary: (res) => ({ rows: rows(['exitCode', String(res?.exitCode ?? '')]), block: res?.stdout }),
      },
      {
        title: '拿公网地址',
        build: (ctx, p) => req('GET', `/v1/sandboxes/${ctx.instanceId}/ports/${Number(p.port) || 8080}`),
        link: (res) => res?.url || null,
      },
      {
        title: '销毁',
        note: '销毁后上面那个地址立刻失效。',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/terminate`, {
          idempotencyKey: `playground:terminate:${crypto.randomUUID()}`,
        }),
      },
      { wait: 'TERMINATED', title: '确认销毁完成' },
    ],
  },
  {
    id: 'suspend',
    title: '挂起省钱',
    blurb:
      '不用的时候挂起，单价掉一个数量级，磁盘还在，恢复后接着用。跑完看花费 —— 运行和挂起是两段分开算的。',
    params: [
      { key: 'maxHourly', label: '每小时价格上限（USD）', type: 'text', default: '0.60' },
    ],
    steps: [
      {
        title: '找一台支持挂起的',
        note: '不是所有服务商都支持主动挂起。比如 Cloudflare 只会自己休眠，没有可调用的挂起动作。',
        build: () => req('GET', '/v1/offerings'),
        after: (res, ctx, params) => {
          const ceiling = Number(params.maxHourly || '0.60');
          const pick = (Array.isArray(res) ? res : [])
            .filter((o) => o?.capabilities?.suspension?.supported
              && o?.capabilities?.suspension?.resumeSupported
              && o?.capabilities?.exec && o?.capabilities?.files)
            .filter((o) => Number(o?.billing?.estimatedHourlyUsdByState?.RUNNING) <= ceiling)
            .sort((a, b) => Number(a?.billing?.estimatedHourlyUsdByState?.RUNNING)
              - Number(b?.billing?.estimatedHourlyUsdByState?.RUNNING))[0];
          if (!pick) throw new Error(`没有满足挂起/文件/执行能力且低于 $${ceiling}/时的规格`);
          ctx.offeringId = pick?.id;
          ctx.pick = pick;
        },
        summary: (res, ctx) => rows(
          ['选中', ctx.pick?.name],
          ['运行', ctx.pick?.billing?.estimatedHourlyUsdByState?.RUNNING
            ? `$${ctx.pick.billing.estimatedHourlyUsdByState.RUNNING}/时` : null],
          ['挂起', ctx.pick?.billing?.estimatedHourlyUsdByState?.SUSPENDED
            ? `$${ctx.pick.billing.estimatedHourlyUsdByState.SUSPENDED}/时` : null],
          ['挂起后保留内存', ctx.pick?.capabilities?.suspension?.preservesMemory ? '是' : '否'],
        ),
      },
      {
        title: '开机',
        build: (ctx) => req('POST', '/v1/sandboxes', {
          selection: { offeringId: ctx.offeringId },
          idempotencyKey: `playground:suspend:${crypto.randomUUID()}`,
          metadata: { client: 'sandbox-playground', scenario: 'suspend-resume' },
        }),
        after: (res, ctx) => {
          ctx.instanceId = res?.id;
          ctx.instanceIds = [...new Set([...(ctx.instanceIds || []), res?.id].filter(Boolean))];
        },
        summary: (res) => rows(['实例', res?.id]),
      },
      { wait: 'RUNNING', title: '等待就绪' },
      {
        title: '写入挂起前的标记文件',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/files`, {
          path: 'lifecycle-marker.txt', encoding: 'utf8', content: 'XAPI_LIFECYCLE_OK=42',
        }),
      },
      {
        title: '挂起',
        note: '消费者只调 /suspend。底层是 PAUSE 还是 STOP 由服务商固定，语义在规格里如实声明。',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/suspend`, {}),
      },
      { wait: 'SUSPENDED', title: '等待挂起完成' },
      {
        title: '恢复',
        note: '文件一定还在。内存和进程是否保留，取决于服务商用的机制。',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/resume`, {}),
      },
      { wait: 'RUNNING', title: '等待恢复' },
      {
        title: '确认挂起后文件仍存在',
        build: (ctx) => req('GET', `/v1/sandboxes/${ctx.instanceId}/files?path=lifecycle-marker.txt`),
        summary: (res) => ({ block: res?.content }),
      },
      {
        title: '销毁',
        build: (ctx) => req('POST', `/v1/sandboxes/${ctx.instanceId}/terminate`, {
          idempotencyKey: `playground:terminate:${crypto.randomUUID()}`,
        }),
      },
      { wait: 'TERMINATED', title: '确认销毁完成' },
    ],
  },
  {
    id: 'rest-api',
    title: '调用一个普通 API 服务',
    blurb:
      'Sandbox 只是计费类型之一。市场上大部分是按次或按 token 计费的普通 API —— 同一把 xAPI Key、同一套网关，' +
      '区别只在计费方式。这里先看目录，再真调一个。',
    params: [
      { key: 'host', label: '服务 host 前缀', type: 'text', default: '',
        hint: '第一步会列出市场上的服务，把想调的那个 host 填进来。最终地址是 ' + API_GATEWAY_TEMPLATE + '。' },
      { key: 'path', label: '接口路径', type: 'text', default: '/',
        hint: '服务自己的路径，比如 /v1/weather。在市场详情页能看到完整端点列表。' },
      { key: 'method', label: '方法', type: 'text', default: 'GET', hint: 'GET / POST / PUT / DELETE。' },
      { key: 'body', label: '请求体（JSON，GET 留空）', type: 'textarea', default: '',
        hint: '留空表示不带请求体。' },
    ],
    steps: [
      {
        title: '市场上有哪些服务',
        note: '公开目录，不需要密钥。挑一个把它的 host 填到上面。',
        base: 'api',
        build: () => req('GET', '/api-services'),
        summary: (res) => ({
          block: (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [])
            .slice(0, 10)
            .map((a) => `${a.host || '—'}   ${a.name}${a.category ? ` · ${a.category}` : ''}`)
            .join('\n') || '（目录为空）',
        }),
      },
      {
        title: '调用它',
        note:
          '这一步打的是服务自己的网关地址，按该端点的计费方式扣费。' +
          '浏览器跨域被拦时，右边「复制调用代码」里的 cURL 一定能跑。',
        absolute: (ctx, p) =>
          API_GATEWAY_TEMPLATE.replace('{host}', String(p.host || '').trim()) +
          (String(p.path || '/').startsWith('/') ? p.path : `/${p.path}`),
        build: (ctx, p) => req(
          String(p.method || 'GET').toUpperCase(),
          (String(p.path || '/').startsWith('/') ? p.path : `/${p.path}`),
          String(p.body || '').trim() ? JSON.parse(p.body) : undefined,
        ),
        summary: (res) => ({
          block: typeof res === 'string' ? res.slice(0, 600) : JSON.stringify(res, null, 2).slice(0, 600),
        }),
      },
    ],
  },
  {
    id: 'catalog',
    title: '跨服务商选型',
    free: false,
    quoteOnly: true,
    blurb:
      '只查目录和报价，不开机、不花钱。目录公开，报价需要 Key；改下面的条件反复试，这是理解选型规则最快的方式。',
    params: [
      {
        key: 'requirements',
        label: '选型条件（JSON）',
        type: 'textarea',
        json: true,
        default: JSON.stringify({ cpu: { min: 1 }, capabilities: ['exec'] }, null, 2),
        hint:
          '可用字段：cpu.min / memoryGiB.min / volumeGiB.min / gpu.{model,count} / regions[] / capabilities[]。' +
          '试试改成 {"gpu":{"count":1}}，会选到另一家。',
      },
    ],
    steps: [
      {
        title: '有哪些可售规格',
        note: '公开接口，不需要密钥。',
        build: () => req('GET', '/v1/offerings'),
        summary: (res) => ({
          block: (Array.isArray(res) ? res : []).slice(0, 8).map((o) =>
            `${o.name} · ${o.resources?.cpu ?? '?'}C/${o.resources?.memoryGiB ?? '?'}G` +
            `${o.resources?.gpu?.length ? ` · ${o.resources.gpu[0].model}` : ''}` +
            ` · $${o.billing?.estimatedHourlyUsdByState?.RUNNING}/时`).join('\n'),
        }),
      },
      {
        title: '按你的条件选型',
        note: '平台在所有服务商里挑一个满足条件且最便宜的。条件不满足时返回 404。',
        build: (ctx, p) => req('POST', '/v1/quotes', { requirements: p.requirements }),
        summary: (res) => rows(
          ['选中', res?.offering?.name],
          ['规格', res?.offering?.resources
            ? `${res.offering.resources.cpu ?? '?'}C / ${res.offering.resources.memoryGiB ?? '?'}G` : null],
          ['GPU', res?.offering?.resources?.gpu?.[0]?.model],
          ['运行单价', res?.offering?.billing?.estimatedHourlyUsdByState?.RUNNING
            ? `$${res.offering.billing.estimatedHourlyUsdByState.RUNNING}/时` : null],
        ),
      },
    ],
  },
];

export const SANDBOX_PLAYGROUND_SCENARIOS = [
  ...EXTRA_SANDBOX_SCENARIOS,
  ...CORE_SCENARIOS.filter((scenario) => scenario.id !== 'rest-api'),
];
const SCENARIOS = SANDBOX_PLAYGROUND_SCENARIOS;

const shellQuote = (s) => `'${String(s).replaceAll("'", `'\\''`)}'`;

/** 把一次真实请求翻译成可直接粘贴的代码。值是实际发出的值，不是占位符。 */
const asCurl = ({ method, path, body, base, url }, needsKey) => {
  const target = url || (base === 'api'
    ? `https://xapi.to/api${path}`
    : `${base ? pinSandboxGateway(PUBLIC_HOST, base) : PUBLIC_HOST}${path}`);
  const lines = [`curl -X ${method} ${target} \\`];
  if (needsKey) lines.push(`  -H 'authorization: Bearer $XAPI_KEY' \\`);
  if (body) {
    lines.push(`  -H 'content-type: application/json' \\`);
    lines.push(`  -d ${shellQuote(JSON.stringify(body))}`);
  } else {
    lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, '');
  }
  return lines.join('\n');
};

const asSdk = ({ method, path, body, url }) => {
  // 普通 API 没有 Sandbox SDK 的语义包装，给 fetch 版本。
  if (url) {
    return `const res = await fetch('${url}', {\n  method: '${method}',\n  headers: { 'authorization': \`Bearer \${process.env.XAPI_KEY}\`` +
      (body ? `, 'content-type': 'application/json' }` : ` }`) +
      (body ? `,\n  body: JSON.stringify(${JSON.stringify(body, null, 2)}),` : '') +
      `\n});\nconsole.log(await res.json());`;
  }
  const m = path.match(/^\/v1\/sandboxes\/([^/]+)\/(.+)$/);
  const arg = (v) => JSON.stringify(v, null, 2).split('\n').join('\n');
  if (path === '/v1/quotes') return `const quote = await sandbox.quote(${arg(body)});`;
  if (path === '/v1/offerings') return `const offerings = await sandbox.offerings();`;
  if (path === '/v1/sandboxes' && method === 'POST') return `const box = await sandbox.create(${arg(body)});`;
  if (!m) return `await sandbox.request('${method}', '${path}'${body ? `, ${arg(body)}` : ''});`;
  const [, , tail] = m;
  if (tail === 'commands') return `const out = await sandbox.exec(box.id, ${arg(body)});\nconsole.log(out.stdout);`;
  if (tail === 'files' && method === 'POST') return `await sandbox.writeFile(box.id, ${arg(body)});`;
  if (tail.startsWith('audit?') && method === 'GET') {
    const kind = new URLSearchParams(tail.split('?')[1]).get('kind');
    return `const audit = await sandbox.audit(box.id, '${kind}', { pageSize: 100 });`;
  }
  if (tail.startsWith('ports/')) return `const { url } = await sandbox.port(box.id, ${tail.split('/')[1]});`;
  if (['suspend', 'resume', 'terminate'].includes(tail)) return `await sandbox.${tail}(box.id);`;
  return `await sandbox.request('${method}', '${path}'${body ? `, ${arg(body)}` : ''});`;
};

const CodePane = ({ spec, needsKey, lang, override }) => {
  const [copied, setCopied] = useState(false);
  const text = useMemo(
    () => override || (lang === 'curl' ? asCurl(spec, needsKey) : asSdk(spec)),
    [lang, spec, needsKey, override],
  );
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* 无剪贴板权限时用户仍可手选 */ }
  };
  return (
    <div className="pg-code-wrap">
      <button type="button" className="pg-copy" data-done={copied} onClick={copy}>
        {copied ? '已复制' : '复制'}
      </button>
      <pre className="code">{text}</pre>
    </div>
  );
};

const RawPane = ({ request, response }) => {
  const fmt = (v) => {
    if (v == null) return '—';
    const s = typeof v === 'string' ? v : JSON.stringify(v, null, 2);
    return s.length > 4000 ? `${s.slice(0, 4000)}\n… (已截断)` : s;
  };
  return (
    <div className="pg-raw-grid">
      <div><div className="pg-raw-cap">Request body</div><pre className="code">{fmt(request)}</pre></div>
      <div><div className="pg-raw-cap">Response body</div><pre className="code">{fmt(response)}</pre></div>
    </div>
  );
};

export const PlaygroundPage = () => {
  const [apiKey, setApiKey] = useState(() => {
    try { return sessionStorage.getItem(KEY_STORAGE) || ''; } catch { return ''; }
  });
  const [activeId, setActiveId] = useState(SCENARIOS[0].id);
  const [runs, setRuns] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [stepSelection, setStepSelection] = useState({});
  const [inspectorTab, setInspectorTab] = useState('curl');
  const [paramState, setParamState] = useState(() =>
    Object.fromEntries(
      SCENARIOS.map((s) => [s.id, Object.fromEntries((s.params || []).map((p) => [p.key, p.default]))]),
    ),
  );
  const abortRef = useRef(false);

  const scenario = useMemo(() => SCENARIOS.find((s) => s.id === activeId) || SCENARIOS[0], [activeId]);
  const state = runs[scenario.id];
  const raw = paramState[scenario.id] || {};

  // JSON 型参数在这里解析一次：解析失败要挡住运行，否则会带着坏参数真发请求。
  const { params, paramError } = useMemo(() => {
    const out = {};
    for (const p of scenario.params || []) {
      if (!p.json) { out[p.key] = raw[p.key]; continue; }
      try { out[p.key] = JSON.parse(raw[p.key] || '{}'); }
      catch (e) { return { params: out, paramError: `${p.label} 不是合法 JSON：${e.message}` }; }
    }
    return { params: out, paramError: null };
  }, [scenario, raw]);

  const canRun = (scenario.free || !!apiKey.trim()) && !paramError;

  const saveKey = (value) => {
    setApiKey(value);
    try {
      if (value) sessionStorage.setItem(KEY_STORAGE, value);
      else sessionStorage.removeItem(KEY_STORAGE);
    } catch { /* 隐私模式不可写 */ }
  };
  const setParam = (key, value) =>
    setParamState((p) => ({ ...p, [scenario.id]: { ...p[scenario.id], [key]: value } }));

  const call = useCallback(async ({ method, path, body, base, url }) => {
    const headers = new Headers({ accept: 'application/json' });
    if (apiKey) headers.set('authorization', `Bearer ${apiKey}`);
    if (body) headers.set('content-type', 'application/json');
    const started = Date.now();
    // base='api' 是控制面（市场目录之类），url 是完整绝对地址（普通 API 的服务网关），
    // 其余默认走 Sandbox 网关。
    const target = url || (base === 'api'
      ? `/api${path}`
      : `${sandboxGateway(base)}${path}`);
    const res = await fetch(target, {
      method, headers, ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const text = await res.text();
    let parsed = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
    const contentType = res.headers.get('content-type') || '';
    if (res.ok && text && !/application\/json/i.test(contentType)) {
      throw new Error(`Sandbox Gateway 返回了非 JSON 响应（${contentType || '未知内容类型'}）`);
    }
    return { status: res.status, ok: res.ok, ms: Date.now() - started, body: parsed };
  }, [apiKey]);

  const run = async () => {
    abortRef.current = false;
    setBusyId(scenario.id);
    setRuns((prev) => ({ ...prev, [scenario.id]: { steps: [], done: false } }));
    const ctx = { instanceIds: [] };
    let emittedSteps = 0;
    const push = (entry) => {
      const index = emittedSteps;
      emittedSteps += 1;
      setStepSelection((value) => ({ ...value, [scenario.id]: index }));
      setInspectorTab('result');
      setRuns((p) => {
        const cur = p[scenario.id] || { steps: [] };
        return { ...p, [scenario.id]: { ...cur, steps: [...cur.steps, entry] } };
      });
    };
    const patch = (delta) =>
      setRuns((p) => {
        const cur = p[scenario.id];
        if (!cur?.steps.length) return p;
        const steps = [...cur.steps];
        steps[steps.length - 1] = { ...steps[steps.length - 1], ...delta };
        return { ...p, [scenario.id]: { ...cur, steps } };
      });

    const idsForRun = () => [...new Set(
      [...(ctx.instanceIds || []), ctx.instanceId].filter(Boolean),
    )];
    const reconcileInstances = async ({ showStep = false } = {}) => {
      const ids = idsForRun();
      if (!ids.length) return;
      if (showStep) push({ title: '异常清理与终态核对', state: 'running' });
      try {
        for (const id of ids) {
          const current = await call({
            method: 'GET', path: `/v1/sandboxes/${id}`, base: scenario.gateway,
          });
          if (!current.ok) throw new Error(`无法读取实例 ${id}（HTTP ${current.status}）`);
          if (current.body?.observedState !== 'TERMINATED') {
            const terminated = await call({
              method: 'POST', path: `/v1/sandboxes/${id}/terminate`,
              base: scenario.gateway,
              body: { idempotencyKey: `playground:cleanup:${crypto.randomUUID()}` },
            });
            if (!terminated.ok && terminated.status !== 409) {
              throw new Error(`销毁实例 ${id} 失败（HTTP ${terminated.status}）`);
            }
          }
        }
        const deadline = Date.now() + 180_000;
        let pending = ids;
        while (pending.length && Date.now() < deadline) {
          const states = await Promise.all(pending.map(async (id) => {
            const got = await call({
              method: 'GET', path: `/v1/sandboxes/${id}`, base: scenario.gateway,
            });
            return { id, state: got.body?.observedState };
          }));
          pending = states.filter(({ state }) => state !== 'TERMINATED').map(({ id }) => id);
          if (pending.length) await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        if (pending.length) throw new Error(`实例未在 180 秒内进入 TERMINATED：${pending.join(', ')}`);
        if (showStep) patch({ state: 'ok', meta: `${ids.length} 台实例均已 TERMINATED` });
      } catch (cleanupError) {
        if (showStep) patch({ state: 'error', meta: cleanupError.message });
        throw cleanupError;
      }
    };

    try {
      for (const step of scenario.steps) {
        if (abortRef.current) break;

        if (step.wait) {
          push({ title: step.title, note: step.note, state: 'running' });
          let seen = null;
          const deadline = Date.now() + 180_000;
          while (Date.now() < deadline && !abortRef.current) {
            const ids = step.ids?.(ctx) || [ctx.instanceId];
            const states = await Promise.all(ids.filter(Boolean).map(async (id) => {
              const got = await call({
                method: 'GET', path: `/v1/sandboxes/${id}`, base: scenario.gateway,
              });
              return got.body?.observedState;
            }));
            seen = states.length === 1 ? states[0] : states.join(' / ');
            patch({ meta: seen || '…' });
            if (states.length && states.every((value) => value === step.wait)) break;
            if (states.some((value) => ['FAILED', 'TERMINATED'].includes(value) && value !== step.wait)) break;
            await new Promise((r) => setTimeout(r, 2000));
          }
          const ok = String(seen || '').split(' / ').every((value) => value === step.wait);
          patch({ state: ok ? 'ok' : 'error', meta: seen || '超时' });
          if (!ok) throw new Error(`未能进入 ${step.wait}（实际 ${seen || '超时'}）`);
          continue;
        }

        let spec = step.build(ctx, params);
        if (step.base || scenario.gateway) spec.base = step.base || scenario.gateway;
        if (step.absolute) spec.url = step.absolute(ctx, params);
        push({
          title: step.title, note: step.note, state: 'running',
          method: spec.method, path: spec.url || spec.path, request: spec.body, spec,
        });
        // 跨域被拦、DNS 不通都会在这里抛，要作为这一步的失败展示出来，
        // 而不是让整个场景静默中断 —— 用户看不到原因就无从下手。
        let res;
        try {
          res = await call(spec);
          if (res.ok && step.until) {
            const deadline = Date.now() + (step.timeoutMs || 180_000);
            let attempts = 1;
            while (!step.until(res.body) && Date.now() < deadline && !abortRef.current) {
              patch({ meta: `${step.pendingText || '等待条件满足'} · 第 ${attempts} 次` });
              await new Promise((resolve) => setTimeout(resolve, step.retryIntervalMs || 2000));
              spec = step.build(ctx, params);
              if (step.base || scenario.gateway) spec.base = step.base || scenario.gateway;
              if (step.absolute) spec.url = step.absolute(ctx, params);
              res = await call(spec);
              attempts += 1;
              if (!res.ok) break;
            }
            if (res.ok && !step.until(res.body)) {
              patch({ state: 'error', meta: `${step.pendingText || '等待条件满足'}超时` });
              throw new Error(`${step.pendingText || '等待条件满足'}超时`);
            }
            res.attempts = attempts;
          }
        } catch (netError) {
          const waitingTimeout = String(netError?.message || '').endsWith('超时');
          patch({
            state: 'error', meta: waitingTimeout ? '等待超时' : '网络错误',
            block: waitingTimeout
              ? netError.message
              : `${netError.message}\n\n浏览器可能拦了跨域请求。用「复制调用代码」里的 cURL 在终端跑同一次调用。`,
          });
          throw netError;
        }
        step.after?.(res.body, ctx, params);
        const s = step.summary?.(res.body, ctx);
        patch({
          state: res.ok ? 'ok' : 'error',
          meta: `${res.status} · ${res.ms}ms${res.attempts > 1 ? ` · ${res.attempts} 次` : ''}`,
          response: res.body,
          rows: Array.isArray(s) ? s : s?.rows,
          block: Array.isArray(s) ? null : s?.block,
          link: step.link?.(res.body) || null,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }

      // 场景步骤应显式销毁；这里再以服务端终态为准做一次安全网核对。
      await reconcileInstances();

      // 成功不只等于命令返回 0：逐台读取操作、状态、用量和结算记录，
      // 让页面展示的 audit 工具合同与真实执行证据保持一致。
      const audit = {};
      const finalIds = idsForRun();
      for (const id of finalIds) {
        audit[id] = {};
        for (const kind of ['operations', 'events', 'usageSegments', 'billingPeriods']) {
          const spec = {
            method: 'GET',
            path: `/v1/sandboxes/${id}/audit?kind=${kind}&pageSize=100`,
            base: scenario.gateway,
          };
          push({
            title: `读取审计 · ${kind}`,
            note: '审计记录属于当前 xAPI 用户，只返回该 Key 所属账户创建的实例。',
            state: 'running', method: spec.method, path: spec.path, spec,
          });
          const res = await call(spec);
          audit[id][kind] = res.body;
          patch({
            state: res.ok ? 'ok' : 'error',
            meta: `${res.status} · ${res.ms}ms`,
            response: res.body,
            rows: [
              { k: '实例', v: id },
              { k: '记录类型', v: kind },
              { k: '记录数', v: String(res.body?.total ?? res.body?.items?.length ?? 0) },
            ],
          });
          if (!res.ok) throw new Error(`读取 ${kind} 审计失败（HTTP ${res.status}）`);
        }
      }

      // 花费从实例账单读，不自己算 —— 页面上的数字必须和账单一致。
      let cost;
      if (finalIds.length) {
        const details = await Promise.all(finalIds.map((id) => call({
          method: 'GET', path: `/v1/sandboxes/${id}`, base: scenario.gateway,
        })));
        cost = details.reduce((total, item) => total + Number(item.body?.totalCost || 0), 0);
      }
      setRuns((p) => ({
        ...p,
        [scenario.id]: { ...p[scenario.id], done: true, cost, audit },
      }));
    } catch (error) {
      let cleanupError = null;
      try { await reconcileInstances({ showStep: true }); }
      catch (caught) { cleanupError = caught; }
      setRuns((p) => ({
        ...p,
        [scenario.id]: {
          ...p[scenario.id], done: true,
          error: cleanupError
            ? `${error.message}；自动清理未完成：${cleanupError.message}`
            : error.message,
        },
      }));
    } finally {
      setBusyId(null);
    }
  };

  // 未运行时展示脚本本身：请求体和调用代码本来就该先让人看清楚。
  const preview = useMemo(() => scenario.steps.map((s) => {
    if (s.wait) return { title: s.title, note: s.note };
    let spec = null;
    try {
      const ctx = { instanceId: '<id>', quoteId: '<quoteId>', offeringId: '<offeringId>' };
      spec = s.build(ctx, params);
      if (s.base || scenario.gateway) spec.base = s.base || scenario.gateway;
      if (s.absolute) spec.url = s.absolute(ctx, params);
    } catch { /* 参数不全（比如 JSON 请求体还没填完）时只显示标题 */ }
    return { title: s.title, note: s.note, method: spec?.method,
             path: spec?.url || spec?.path, request: spec?.body, spec };
  }), [scenario, params]);
  const shown = state?.steps?.length ? state.steps : preview;
  const selectedIndex = Math.min(
    stepSelection[scenario.id] ?? 0,
    Math.max(0, shown.length - 1),
  );
  const selected = shown[selectedIndex] || {};
  const scenarioSdkExample = useMemo(
    () => (typeof scenario.sdkExample === 'function' ? scenario.sdkExample(params) : scenario.sdkExample),
    [scenario, params],
  );
  const completedCount = shown.filter((entry) => entry.state === 'ok').length;
  const failedCount = shown.filter((entry) => entry.state === 'error').length;
  const inspectorTabs = [
    ['result', '结果'], ['curl', 'cURL'], ['sdk', 'SDK'], ['raw', 'Raw'],
  ];

  return (
    <main className="pg">
      <aside className="pg-rail">
        <div>
          <div className="pg-rail-title">Sandbox Playground</div>
          <nav className="pg-nav" style={{ marginTop: 10 }}>
            {SCENARIOS.map((s, i) => (
              <button
                key={s.id} type="button" className="pg-nav-item"
                aria-current={s.id === activeId} onClick={() => setActiveId(s.id)}
              >
                <span className="pg-nav-index">{i + 1}</span>
                <span className="pg-nav-label">{s.title}</span>
                {s.free && <span className="pg-nav-free">免费</span>}
              </button>
            ))}
          </nav>
        </div>

        {/* 包一层 form 并吃掉 submit：游离的 password 输入框会被浏览器当成登录表单，
            回车或某些按钮点击会触发导航，把人从 playground 弹走。autoComplete 关掉
            避免密码管理器来填。 */}
        <form className="pg-key" onSubmit={(e) => e.preventDefault()}>
          <label>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>xAPI Key</span>
            <input
              className="input mono" type="password" placeholder="xapi_..."
              autoComplete="off" name="xapi-playground-key"
              value={apiKey} onChange={(e) => saveKey(e.target.value)}
            />
          </label>
          <p className="pg-key-hint" style={{ margin: '7px 0 0' }}>
            只存在当前标签页。会真实创建云资源并按秒计费，每个场景最后一步都会销毁。
          </p>
        </form>
      </aside>

      <div className="pg-main">
        <header className="pg-studio-head">
          <div>
            <span className="micro">SANDBOX STUDIO</span>
            <h1>真实云环境工作台</h1>
            <p>配置场景，逐项检查请求，再从固定检查器中查看结果、代码和原始报文。</p>
            <p className="pg-scope-note">
              xAPI 提供 Sandbox 资源、能力、计费与审计；场景中的 Agent、CI 和数据流程由客户端组合，不在 Gateway 内编排。
            </p>
          </div>
          <details className="pg-ai-guide">
            <summary>AI 如何使用 Sandbox</summary>
            <div className="pg-ai-contract" aria-label="AI Sandbox 工具">
              {AI_TOOL_CONTRACT.map(([name, description]) => (
                <div key={name}><code>{name}</code><span>{description}</span></div>
              ))}
            </div>
          </details>
        </header>

        <div className="pg-workbench">
          <section className="pg-config" aria-label="场景配置">
            <div className="pg-config-head">
              <span className="pg-section-cap">Scenario</span>
              <h2>{scenario.title}</h2>
              <p>{scenario.blurb}</p>
            </div>

            {!!scenario.params?.length && (
              <div className="pg-params">
                <div className="pg-params-head">
                  <span className="pg-params-title">参数</span>
                  <button
                    type="button" className="pg-code-tab"
                    onClick={() => setParamState((p) => ({
                      ...p,
                      [scenario.id]: Object.fromEntries(scenario.params.map((x) => [x.key, x.default])),
                    }))}
                  >
                    恢复默认
                  </button>
                </div>
                {scenario.params.map((p) => (
                  <div className="pg-param" key={p.key}>
                    <label>
                      <span className="pg-param-name"><b>{p.label}</b></span>
                      {p.type === 'textarea' ? (
                        <textarea
                          className="input mono" spellCheck={false}
                          aria-label={p.label}
                          value={raw[p.key] ?? ''} onChange={(e) => setParam(p.key, e.target.value)}
                        />
                      ) : (
                        <input
                          className="input mono" aria-label={p.label} value={raw[p.key] ?? ''}
                          onChange={(e) => setParam(p.key, e.target.value)}
                        />
                      )}
                    </label>
                    {p.hint && <p className="pg-param-hint" style={{ margin: '5px 0 0' }}>{p.hint}</p>}
                  </div>
                ))}
                {paramError && <div className="pg-param-err">{paramError}</div>}
              </div>
            )}

            <button
              type="button" className="btn pg-run"
              disabled={!canRun || busyId != null}
              title={paramError || (canRun ? '' : '需要先填 xAPI Key')}
              onClick={run}
            >
              {busyId === scenario.id ? '运行中…' : state ? '重新运行场景' : '运行真实场景'}
            </button>

            {state?.done && (
              <div className="pg-outcome">
                <span style={{ color: state.error ? 'var(--err)' : 'var(--ok)' }}>
                  {state.error
                    ? `中断：${state.error}（已尝试销毁实例）`
                    : scenario.quoteOnly ? '完成：只查询目录与报价' : '完成：实例已销毁'}
                </span>
                {state.cost != null && <span>花费 <b className="mono">{money(state.cost)}</b></span>}
              </div>
            )}
          </section>

          <section className="pg-session" aria-label="运行会话">
            <header className="pg-session-head">
              <div>
                <span className="pg-section-cap">Run session</span>
                <strong>{state ? (state.done ? '执行完成' : '执行中') : '请求预览'}</strong>
              </div>
              <div className="pg-session-stats mono">
                <span>{shown.length} requests</span>
                {!!completedCount && <span className="is-ok">{completedCount} passed</span>}
                {!!failedCount && <span className="is-error">{failedCount} failed</span>}
              </div>
            </header>

            <div className="pg-session-grid">
              <nav className="pg-requests" aria-label="请求步骤">
                {shown.map((entry, i) => (
                  <button
                    type="button" key={`${scenario.id}-${i}`}
                    className="pg-request-item" data-state={entry.state || ''}
                    aria-current={i === selectedIndex}
                    onClick={() => { setStepSelection((v) => ({ ...v, [scenario.id]: i })); setInspectorTab('result'); }}
                  >
                    <span className="pg-request-index mono">{String(i + 1).padStart(2, '0')}</span>
                    <span className="pg-request-copy">
                      <strong>{entry.title}</strong>
                      <span className="mono">{entry.method ? `${entry.method} ${entry.path}` : entry.meta || 'lifecycle check'}</span>
                    </span>
                    <span className="pg-request-state" aria-label={entry.state || 'preview'} />
                  </button>
                ))}
              </nav>

              <article className="pg-inspector">
                <header className="pg-inspector-head">
                  <div>
                    <span className="pg-section-cap">Request {String(selectedIndex + 1).padStart(2, '0')}</span>
                    <h3>{selected.title}</h3>
                  </div>
                  {selected.meta && <span className="pg-step-meta mono">{selected.meta}</span>}
                </header>
                {selected.note && <p className="pg-step-note">{selected.note}</p>}
                {selected.method && (
                  <div className="pg-endpoint mono"><b>{selected.method}</b><span>{selected.path}</span></div>
                )}

                <div className="pg-inspector-tabs" role="tablist" aria-label="请求检查器">
                  {inspectorTabs.map(([id, label]) => {
                    const disabled = id === 'curl'
                      ? !selected.spec
                      : id === 'sdk' && !selected.spec && !scenarioSdkExample;
                    return (
                      <button
                        key={id} type="button" role="tab" className="pg-inspector-tab"
                        aria-selected={inspectorTab === id} disabled={disabled}
                        onClick={() => setInspectorTab(id)}
                      >{label}</button>
                    );
                  })}
                </div>

                <div className="pg-inspector-body" role="tabpanel">
                  {inspectorTab === 'result' && (
                    <div className="pg-result-view">
                      {selected.rows?.map(({ k, v }) => (
                        <div className="pg-result-row" key={k}><span>{k}</span><b className="mono">{v}</b></div>
                      ))}
                      {selected.block && <pre className="code">{selected.block}</pre>}
                      {selected.link && (
                        <a className="pg-result-link" href={selected.link} target="_blank" rel="noreferrer noopener">在浏览器打开预览 ↗</a>
                      )}
                      {!selected.rows?.length && !selected.block && !selected.link && (
                        <div className="pg-empty-result">
                          <span>{selected.state === 'running' ? '正在等待服务响应…' : selected.state === 'ok' ? '请求已成功完成' : '尚未执行'}</span>
                          <small>{state ? '选择 Raw 查看完整响应。' : '当前展示按参数生成的真实请求；运行后会在这里显示结果。'}</small>
                        </div>
                      )}
                    </div>
                  )}
                  {(inspectorTab === 'curl' && selected.spec) && (
                    <CodePane spec={selected.spec} needsKey={!scenario.free} lang="curl" />
                  )}
                  {(inspectorTab === 'sdk' && (selected.spec || scenarioSdkExample)) && (
                    <CodePane
                      spec={selected.spec} needsKey={!scenario.free} lang="sdk"
                      override={scenarioSdkExample}
                    />
                  )}
                  {inspectorTab === 'raw' && <RawPane request={selected.request} response={selected.response} />}
                </div>
              </article>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default PlaygroundPage;
